// A terraform job's module and provider versions in every env/region it was
// applied to, from cd-pipeline-infra's terraform-stacks table.

import React, { useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ViewModule as ModulesIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useDeploymentData } from '../hooks/useDeploymentData';
import {
  buildModuleMatrix,
  buildProviderMatrix,
  providerLabel,
  shortCommit,
  stackModulesKey,
  versionLabel,
  VersionsByEnv,
} from '../lib/stackModules';
import { envRegionLabel } from '../lib/rollbackProfiles';
import { formatTimestamp, formatTimestampShort } from '../lib/format';
import { getColors, rounded, sticker } from '../theme/tokens';
import { tagStyle } from '../theme/statusTokens';
import { TerraformStack } from '../../types/DeploymentRecord';
import EmptyState from './EmptyState';
import StatusPill from './StatusPill';

const mono = {
  fontFamily: 'ui-monospace, Menlo, monospace',
  fontSize: '0.78rem',
};

export interface StackModulesSectionProps {
  app: string;
  jobId: string;
}

const StackModulesSection: React.FC<StackModulesSectionProps> = ({ app, jobId }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { wsStatus, stackModules, fetchStackModules } = useDeploymentData();

  useEffect(() => {
    if (wsStatus === 'connected' && app && jobId) fetchStackModules(app, jobId);
  }, [wsStatus, app, jobId, fetchStackModules]);

  const slice = stackModules.get(stackModulesKey(app, jobId));
  const stacks = useMemo(() => slice?.stacks || [], [slice?.stacks]);
  const modules = useMemo(() => buildModuleMatrix(stacks), [stacks]);
  const providers = useMemo(() => buildProviderMatrix(stacks), [stacks]);
  const byEnv = useMemo(() => new Map(stacks.map(s => [s.env_region, s])), [stacks]);

  let body: React.ReactNode;
  if (!slice || (slice.loading && !slice.loaded)) {
    body = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: c.inkMuted,
        }}
      >
        <CircularProgress size={14} />
        <Typography variant="body2">Loading module inventory…</Typography>
      </Box>
    );
  } else if (slice.error) {
    body = <Alert severity="error">{slice.error}</Alert>;
  } else if (stacks.length === 0) {
    body = (
      <EmptyState
        icon={<ModulesIcon />}
        title="No module inventory yet"
        body="Recorded on the next terraform deploy."
      />
    );
  } else {
    const differing = modules.rows.filter(r => r.differs).length;
    body = (
      <>
        <Typography variant="body2" sx={{ color: c.inkSecondary, mb: 1 }}>
          {modules.rows.length} module{modules.rows.length === 1 ? '' : 's'}
          {stacks.length > 1
            ? ` · ${differing} differ${differing === 1 ? 's' : ''} across environments`
            : ''}
        </Typography>
        <MatrixTable
          label="Module versions by environment"
          leading={['Module', 'Source', 'Constraint']}
          columns={modules.columns}
          stacks={byEnv}
          rows={modules.rows.map(r => ({
            id: r.key,
            depth: r.depth,
            differs: r.differs,
            versions: r.versions,
            local: true,
            cells: [
              <Box
                key="key"
                component="span"
                title={r.key}
                sx={{
                  ...mono,
                  display: 'inline-block',
                  pl: r.depth * 2,
                  color: r.depth ? c.inkSecondary : c.ink,
                }}
              >
                {r.depth > 0 && (
                  <Box component="span" sx={{ color: c.inkFaint, mr: 0.5 }}>
                    └
                  </Box>
                )}
                {r.depth > 0 ? r.key.split('.').pop() : r.key}
              </Box>,
              <Box
                key="source"
                component="span"
                sx={{ ...mono, color: c.inkMuted, overflowWrap: 'anywhere' }}
              >
                {r.source}
              </Box>,
              <Box key="constraint" component="span" sx={mono}>
                {r.constraint || '—'}
              </Box>,
            ],
          }))}
        />
        <Typography
          variant="overline"
          sx={{ color: c.inkMuted, display: 'block', mt: 2.5, mb: 0.5 }}
        >
          Providers
        </Typography>
        {providers.rows.length > 0 ? (
          <MatrixTable
            label="Provider versions by environment"
            leading={['Provider']}
            columns={providers.columns}
            stacks={byEnv}
            rows={providers.rows.map(r => ({
              id: r.address,
              depth: 0,
              differs: r.differs,
              versions: r.versions,
              local: false,
              cells: [
                <Box key="address" component="span" title={r.address} sx={mono}>
                  {providerLabel(r.address)}
                </Box>,
              ],
            }))}
          />
        ) : (
          <Typography variant="body2" sx={{ color: c.inkMuted }}>
            No providers recorded.
          </Typography>
        )}
      </>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        mt: 2,
        borderRadius: `${rounded.lg}px`,
        borderColor: c.hairline,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: c.ink }}>Modules</Typography>
      <Typography variant="body2" sx={{ color: c.inkMuted, mb: 2 }}>
        Module and provider versions this job runs in each environment, as of its last successful
        apply. Highlighted rows differ between environments.
      </Typography>
      {body}
    </Paper>
  );
};

interface MatrixRow {
  id: string;
  depth: number;
  differs: boolean;
  versions: VersionsByEnv;
  /** Whether an empty version means a local module. */
  local: boolean;
  cells: React.ReactNode[];
}

const MatrixTable: React.FC<{
  label: string;
  leading: string[];
  columns: string[];
  stacks: Map<string, TerraformStack>;
  rows: MatrixRow[];
}> = ({ label, leading, columns, stacks, rows }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const diff = tagStyle(sticker.orange, theme.palette.mode);

  return (
    <Box
      sx={{
        overflowX: 'auto',
        border: `1px solid ${c.hairline}`,
        borderRadius: `${rounded.md}px`,
      }}
    >
      <Table
        size="small"
        aria-label={label}
        sx={{
          '& th': {
            color: c.inkMuted,
            fontWeight: 600,
            backgroundColor: c.canvasSoft,
            verticalAlign: 'bottom',
          },
          '& td, & th': { borderColor: c.hairline },
        }}
      >
        <TableHead>
          <TableRow>
            {leading.map(h => (
              <TableCell key={h}>{h}</TableCell>
            ))}
            {columns.map(env => (
              <TableCell key={env} sx={{ whiteSpace: 'nowrap' }}>
                <EnvHeader envRegion={env} stack={stacks.get(env)} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(r => (
            <TableRow
              key={r.id}
              data-differs={String(r.differs)}
              data-depth={r.depth}
              sx={r.differs ? { backgroundColor: diff.bg } : undefined}
            >
              {r.cells.map((cell, i) => (
                <TableCell key={i} sx={{ maxWidth: i === 1 ? 320 : undefined }}>
                  {cell}
                </TableCell>
              ))}
              {columns.map(env => {
                const v = r.versions[env];
                return (
                  <TableCell
                    key={env}
                    sx={{
                      ...mono,
                      whiteSpace: 'nowrap',
                      color: v === undefined ? c.inkFaint : r.differs ? diff.text : c.ink,
                      fontWeight: r.differs && v !== undefined ? 600 : undefined,
                    }}
                  >
                    {v === undefined ? '—' : r.local ? versionLabel(v) : v}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const EnvHeader: React.FC<{ envRegion: string; stack?: TerraformStack }> = ({
  envRegion,
  stack,
}) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const meta = {
    display: 'block',
    color: c.inkMuted,
    fontWeight: 400,
    lineHeight: 1.4,
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography
          variant="body2"
          component="span"
          sx={{ fontWeight: 600, color: c.inkSecondary }}
        >
          {envRegionLabel(envRegion)}
        </Typography>
        {stack?.modules_origin === 'observed' && (
          <Tooltip
            title="Read by a drift run's init: what would install today, not necessarily what is applied."
            arrow
          >
            <span>
              <StatusPill label="observed" hue={sticker.teal} />
            </span>
          </Tooltip>
        )}
      </Box>
      {stack?.release_version && (
        <Typography variant="caption" sx={meta}>
          {stack.release_version}
        </Typography>
      )}
      {stack?.source_commit && (
        <Typography
          variant="caption"
          title={stack.source_commit}
          sx={{ ...meta, ...mono, fontSize: '0.72rem' }}
        >
          {shortCommit(stack.source_commit)}
        </Typography>
      )}
      {stack?.applied_at && (
        <Typography variant="caption" title={formatTimestamp(stack.applied_at)} sx={meta}>
          {formatTimestampShort(stack.applied_at)}
        </Typography>
      )}
    </Box>
  );
};

export default StackModulesSection;
